import { useEffect, useRef } from 'react';

import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { hasFullName, needsCompanyName } from 'entities/user/lib/profileCompletion';
import { type CompleteProfileValues, useCompleteProfile } from 'features/completeProfile/lib/useCompleteProfile';
import { DashboardEvent } from 'features/dashboard/lib/constants';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { useAuth } from 'shared/lib/auth/useAuth';
import { Form } from 'shared/lib/forms/Form';
import { isRequired } from 'shared/lib/validation/isRequired';
import { AuthHeroLayout } from 'shared/ui/AuthHeroLayout';
import { TextFormField } from 'shared/ui/TextFormField';

/** Asks for whatever the Auth0 "Set up your profile" form should have collected after signup but didn't. */
export const CompleteProfile = () => {
  const { user } = useAuth();
  const profile = user?.profile;
  const askName = !hasFullName(profile);
  const askCompanyName = needsCompanyName(profile);
  const askedFields = { asked_name: askName, asked_company_name: askCompanyName };

  // Once per mount: a profile refetch while the step is open must not count as a second show.
  const shownTrackedRef = useRef(false);
  useEffect(() => {
    if (shownTrackedRef.current) {
      return;
    }
    shownTrackedRef.current = true;
    trackEvent(DashboardEvent.COMPLETE_PROFILE_SHOW, { asked_name: askName, asked_company_name: askCompanyName });
  }, [askName, askCompanyName]);

  const formMethods = useForm<CompleteProfileValues>({
    mode: 'onChange',
    defaultValues: {
      first_name: profile?.first_name ?? '',
      last_name: profile?.last_name ?? '',
      company_name: '',
    },
  });

  const { mutate, isPending, error } = useCompleteProfile({ profile, askName, askCompanyName });

  return (
    <AuthHeroLayout>
      <Form
        className="flex flex-col gap-6"
        error={error}
        formMethods={formMethods}
        onSubmit={(data) => {
          trackEvent(DashboardEvent.COMPLETE_PROFILE_SUBMIT_CLICK, askedFields);
          mutate(data);
        }}
      >
        <Typography variant="h1" fontSize={{ xs: 32, sm: 44 }} fontWeight={500} lineHeight={1.1} className="mb-4">
          Set up your profile
        </Typography>
        {askName && (
          <>
            <TextFormField
              autoFocus
              autoComplete="given-name"
              label="First name"
              name="first_name"
              rules={{ validate: { required: isRequired('First name is required') } }}
            />
            <TextFormField
              autoComplete="family-name"
              label="Last name"
              name="last_name"
              rules={{ validate: { required: isRequired('Last name is required') } }}
            />
          </>
        )}
        {askCompanyName && (
          <TextFormField
            autoFocus={!askName}
            autoComplete="organization"
            label="Company name"
            name="company_name"
            rules={{ validate: { required: isRequired('Company name is required') } }}
          />
        )}
        <LoadingButton fullWidth loading={isPending} size="large" type="submit" variant="contained">
          Continue
        </LoadingButton>
      </Form>
    </AuthHeroLayout>
  );
};
